import cn from "classnames";
import { getNumberStructure, isValidCarNumber } from "helpers/carnumber";
import { dateTimeToView, humanizedTimeRange } from "helpers/date";
import { bidStatusesEnum } from "helpers/enums";
import { enumLabel } from "helpers/system";
import Tooltip from "rc-tooltip";
import React, { useState } from "react";
import { useValue } from "../../../realtime";
import CarDropd from "./dropd";
import CarIcons from "./icons";
import CarInfo from "./info";

const CarRenderer = (props) => {
    const { item, enums, disableCarDropd } = props;

    const [openedDropd, setOpenedDropd] = useState(false);

    const realtimeItem = useValue({
        listenEventNames: [
            ...(item.bidId && item.carId ? [`data[${item.bidId}&${item.carId}]`] : []),
            ...(item.carId ? [`data[carId(${item.carId})]`] : []),
            ...(item.bidDriverId ? [`data[bidDriverId(${item.bidDriverId})]`] : []),
            ...(item.bidTrailerId ? [`data[bidTrailerId(${item.bidTrailerId})]`] : []),
        ],

        originalValue: item,
    });

    const carNumber = getNumberStructure(realtimeItem.number);
    const isPlannedBid = realtimeItem.bidStatus === bidStatusesEnum.Planned;
    const isDraft = realtimeItem.bidStatus === bidStatusesEnum.New;
    const carnumberCname = cn("carnumber__wrap", {
        "carnumber__wrap--lightgray": Boolean(realtimeItem.bookingComment),
        "carnumber__wrap--gray carnumber__wrap--disabled":
            !isPlannedBid && !realtimeItem.carIsActive && !realtimeItem.bookingComment,
        "carnumber__wrap--green":
            !isPlannedBid && realtimeItem.carIsActive && realtimeItem.hasPlannedBid && !realtimeItem.bookingComment,
        "carnumber__wrap--blue":
            !isPlannedBid &&
            realtimeItem.carIsActive &&
            realtimeItem.isLastBidPointVisited &&
            !realtimeItem.hasPlannedBid &&
            !realtimeItem.withoutBidPeriod &&
            !realtimeItem.bookingComment,
        "carnumber__wrap--yellow":
            !isPlannedBid &&
            realtimeItem.carIsActive &&
            !realtimeItem.isLastBidPointVisited &&
            !realtimeItem.hasPlannedBid &&
            !realtimeItem.withoutBidPeriod &&
            !realtimeItem.bookingComment,
        "carnumber__wrap--red":
            !isPlannedBid && realtimeItem.carIsActive && realtimeItem.withoutBidPeriod && !realtimeItem.bookingComment,
        "carnumber__wrap--planned": isPlannedBid,
        "carnumber__wrap--draft": isDraft,
        "carnumber__wrap--noclickable": disableCarDropd,
    });

    const openDropd = () => {
        if (disableCarDropd || openedDropd) {
            return;
        }
        setOpenedDropd(true);
        setTimeout(() => document.addEventListener("click", closeDropd), 0);
    };

    const closeDropd = () => {
        setOpenedDropd(false);
        document.removeEventListener("click", closeDropd);
    };

    const overlay = (
        <>
            {isPlannedBid && <h6>Выгрузка по запланированной заявке</h6>}
            {isDraft && <h6>Черновик</h6>}
            {!realtimeItem.carIsActive && (
                <div className="pb-2">
                    <h6>Машина не активна</h6>
                    <div>
                        <strong>Период:</strong> {dateTimeToView(realtimeItem.carInactiveSince)} &mdash;{" "}
                        {dateTimeToView(realtimeItem.carInactiveUntil)}
                    </div>
                    <div>
                        <strong>Причина:</strong> {realtimeItem.carInactivityReason}
                    </div>
                </div>
            )}
            {realtimeItem.carLogistDisplayName && (
                <div>
                    <strong>Логист:</strong> {realtimeItem.carLogistDisplayName}
                </div>
            )}
            {realtimeItem.responsibleDisplayName && (
                <div>
                    <strong>Ответственный:</strong> {realtimeItem.responsibleDisplayName}
                </div>
            )}
            {!realtimeItem.withoutBidPeriod
                ? realtimeItem.bidDriverDisplayName && (
                      <div>
                          <div>
                              <strong>Водитель:</strong> {realtimeItem.bidDriverDisplayName}
                          </div>
                          {realtimeItem.driverRecentRestTime && (
                              <div className="pl-3">
                                  <strong>Отработано за последние 48ч:</strong>{" "}
                                  {humanizedTimeRange(realtimeItem.driverRecentWorkTime)}
                              </div>
                          )}
                          {realtimeItem.driverRecentWorkTime && (
                              <div className="pl-3">
                                  <strong>Отдых за последние 48ч:</strong>{" "}
                                  {humanizedTimeRange(realtimeItem.driverRecentRestTime)}
                              </div>
                          )}
                      </div>
                  )
                : ""}
            {realtimeItem.bidTrailerId && (
                <div>
                    <strong>Прицеп:</strong> {realtimeItem.bidTrailerNumber}{" "}
                    {realtimeItem.bidTrailerTypeId
                        ? `(${enumLabel(enums.trailerType, realtimeItem.bidTrailerTypeId)})`
                        : ""}
                </div>
            )}
            {realtimeItem.carGroups?.length > 0 && (
                <div>
                    <strong>Логистические группы:</strong>
                    <ul className="mb-0">
                        {realtimeItem.carGroups.map((item) => (
                            <li key={item.id}>{item.name}</li>
                        ))}
                    </ul>
                </div>
            )}
            {!isDraft &&
                (realtimeItem.hasPlannedBid ? (
                    <div>Для данной машины запланирована заявка по приезду в город</div>
                ) : realtimeItem.withoutBidPeriod ? (
                    <div>Простаивает в городе без заявки</div>
                ) : (
                    <div>Для данной машины не запланирована заявка по приезду в город</div>
                ))}
        </>
    );

    return (
        <div className="pb-1 c-dropd">
            <div className="overflow-auto">
                <Tooltip destroyTooltipOnHide overlay={overlay} overlayClassName="tooltip--sm">
                    <div className={carnumberCname} onClick={disableCarDropd ? undefined : openDropd}>
                        {isValidCarNumber(realtimeItem.number) ? (
                            <>
                                <div className="carnumber__number">
                                    {carNumber.number.letters[0]}&nbsp;{carNumber.number.number}&nbsp;
                                    {carNumber.number.letters[1]}
                                </div>
                                <div className="carnumber__region carnumber__region--small">{carNumber.region}</div>
                            </>
                        ) : (
                            <div className="carnumber__number carnumber__number--without-region">
                                {realtimeItem.number}
                            </div>
                        )}
                    </div>
                </Tooltip>
            </div>
            {!disableCarDropd && openedDropd && <CarDropd {...props} item={{ ...realtimeItem, hasBid: true }} />}
            <CarInfo {...props} item={realtimeItem} />
            <CarIcons {...props} item={realtimeItem} />
        </div>
    );
};

export default CarRenderer;
